import { useForm } from "react-hook-form";

import { ArrowRight, CheckCircle } from "lucide-react";
import { formContent } from "../../content/content";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import API_URL from "../../../config";



const CbgQuoteForm = () => {
   const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const onSubmit = async (data, e) => {
    try {
      const response = await fetch(`${API_URL}/submit-form`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });

      const result = await response.json();

      if (response.ok) {
        toast.success(result.message || "Message Sent Successfully!");

        // Clear the form using the event reference
        if (e && e.target) {
          e.target.reset();
        }
      } else {
        toast.error(`Failed to submit form: ${result.error}`);
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Error submitting form. Please try again.");
    }
  };

  useEffect(() => {
    const elements = document.querySelectorAll(
      ".fade-in, .slide-up, .title-animate, .desc-animate, .card-animation"
    );

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
          }
        });
      },
      { threshold: 0.2 }
    );

    elements.forEach((el) => observer.observe(el));

    return () => elements.forEach((el) => observer.unobserve(el));
  }, []);

  return (
    <div className="relative min-h-screen bg-green-600 flex items-center justify-center px-5 md:px-0 py-12">
      <div className="absolute inset-0 bg-green-700 opacity-30"></div>
      <div className="max-w-6xl w-full grid grid-cols-1 lg:grid-cols-2 gap-10 relative z-10 fade-in">
        {/* Left Section */}
        <div className="text-white ">
          <h5 className="text-sm font-semibold uppercase desc-animate">
            {formContent.heading.subheading}
          </h5>
          <h1 className="text-4xl font-bold mt-2 title-animate ">
            {formContent.heading.mainTitle}
          </h1>
          <p className="mt-4 text-lg desc-animate">
            {formContent.heading.paragraph}
          </p>

          {/* Bullet Points */}
          <div className="mt-6 space-y-2 ">
            <p className="flex items-center gap-2   fade-in">
              <CheckCircle className="text-white w-5 h-5" /> Reliable and High
              Performance
            </p>
            <p className="flex items-center gap-2  fade-in">
              <CheckCircle className="text-white w-5 h-5" /> 50% More Efficient
              Output
            </p>
            <p className="flex items-center gap-2   fade-in">
              <CheckCircle className="text-white w-5 h-5" /> CBG Material
              Financing Available
            </p>
            <p className="flex items-center gap-2   fade-in">
              <CheckCircle className="text-white w-5 h-5 " /> Built Using
              Advanced Technology
            </p>
            <p className="flex items-center gap-2 fade-in">
              <CheckCircle className="text-white w-5 h-5" /> In-Time
              Manufacturing & dDelivery
            </p>
          </div>

          <div className="mt-6 flex flex-col sm:flex-row gap-4">
            <button
              onClick={() =>
                (navigate("/faqs"))
              }
              className="bg-white border border-gray-800 text-black px-6 py-4 font-semibold flex items-center justify-center gap-2 text-center shadow-lg hover:bg-gray-800 hover:text-white transition-all duration-300 slide-up "
            >
              {formContent.buttons.learnMore} <ArrowRight className="w-5 h-5" />
            </button>

            <button
              onClick={() =>
                (navigate("/faqs"))
              }
              className="bg-white border border-gray-800 text-black px-6 py-4 font-semibold text-center shadow-lg hover:bg-gray-800 hover:text-white transition-all duration-300 slide-up"
            >
              {formContent.buttons.coreValues}
            </button>
          </div>

          <p className="mt-6 text-sm font-medium desc-animate">
            {formContent.quoteText}
          </p>
        </div>

        {/* Right Section - Form */}
        <div className="bg-white p-6 md:p-8 rounded-xl  shadow-lg">
          <h3 className="text-2xl font-bold title-animate">
            {formContent.title}
          </h3>
          <p className="text-gray-600 mt-2 desc-animate">
            {formContent.description}
          </p>
          <form onSubmit={handleSubmit(onSubmit)} className="mt-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6  ">
              {/* Gas Consumption */}
              <div className="flex flex-col slide-up">
                <label className="font-semibold text-sm text-gray-700 title-animate">
                  {formContent.fields.gasConsumption}
                </label>
                <input
                  type="text"
                  {...register("gasConsumption", {
                    required: "This field is required",
                  })}
                  className="w-full border text-sm text-gray-600 rounded-lg p-3 mt-1 fade-in"
                  placeholder={formContent.placeholders.gasConsumption}
                />
                {errors.gasConsumption && (
                  <p className="text-red-500 text-sm desc-animate">
                    {errors.gasConsumption.message}
                  </p>
                )}
              </div>

              {/* Delivery Time */}
              <div className="flex flex-col slide-up">
                <label className="font-semibold text-sm text-gray-700 title-animate">
                  {formContent.fields.deliveryTime}
                </label>
                <select
                  {...register("deliveryTime", {
                    required: "This field is required",
                  })}
                  className="w-full border text-sm text-gray-600 rounded-lg p-3 mt-1  fade-in"
                >
                  <option>Within 1 Week</option>
                  <option>1-2 Weeks</option>
                  <option>1 Month</option>
                  <option>More than a month</option>
                </select>
                {errors.deliveryTime && (
                  <p className="text-red-500 text-sm fade-in">
                    {errors.deliveryTime.message}
                  </p>
                )}
              </div>

              {/* Usage Purpose */}
              <div className="flex flex-col slide-up">
                <label className="font-semibold text-sm text-gray-700 desc-animate">
                  {formContent.fields.usagePurpose}
                </label>
                <select
                  {...register("usagePurpose", {
                    required: "This field is required",
                  })}
                  className="w-full border text-sm text-gray-600 rounded-lg p-3 mt-1 fade-in "
                >
                  <option>Industrial Use</option>
                  <option>Commercial Use</option>
                  <option>Automobile Fuel</option>
                  <option>Power Generation</option>
                  <option>Other</option>
                </select>
                {errors.usagePurpose && (
                  <p className="text-red-500 text-sm desc-animate">
                    {errors.usagePurpose.message}
                  </p>
                )}
              </div>

              {/* Supply Type */}
              <div className="flex flex-col slide-up ">
                <label className="font-semibold text-sm text-gray-700 desc-animate">
                  {formContent.fields.supplyType}
                </label>
                <select
                  {...register("supplyType", {
                    required: "This field is required",
                    alert: " please fill the field ",
                  })}
                  className="w-full border text-sm text-gray-600 rounded-lg p-3 mt-1 fade-in"
                >
                  <option>Bulk Supply</option>
                  <option>Regular Deliveries</option>
                  <option>On-Demand Supply</option>
                </select>
                {errors.supplyType && (
                  <p className="text-red-500 text-sm desc-animate">
                    {errors.supplyType.message}
                  </p>
                )}
              </div>

              {/* Contract Duration */}
              <div className="flex flex-col slide-up">
                <label className="font-semibold text-sm text-gray-700 desc-animate">
                  {formContent.fields.contractDuration}
                </label>
                <select
                  {...register("contractDuration", {
                    required: "This field is required",
                    alert: "",
                  })}
                  className="w-full border text-sm text-gray-600 rounded-lg p-3 mt-1 fade-in"
                >
                  <option>Short-term (1-6 months)</option>
                  <option>Medium-term (6-12 months)</option>
                  <option>Long-term (1 year or more)</option>
                </select>
                {errors.contractDuration && (
                  <p className="text-red-500 text-sm desc-animate">
                    {errors.contractDuration.message}
                  </p>
                )}
              </div>

              
              {/*  Phone Input */}
                <div>
                  <label className="font-semibold text-sm text-gray-700 title-animate">
                    {formContent.fields.mobile}
                  </label>
                  <input
                    type="tel"
                    {...register("contactMethod", {
                      required: "This field is required",
                      pattern: {
                         value: /^[6-9]\d{9}$/,

                       message: "Please enter a valid 10-digit mobile number",
                      },
                    })}
                    className="focus:border-blue-500 border text-start text-sm text-gray-600 rounded-lg px-7 py-3 fade-in"
                    placeholder={formContent.placeholders.mobile}
                  />
                  {errors.contactMethod && (
                    <p className="text-red-500 text-sm desc-animate">
                      {errors.contactMethod.message}
                    </p>
                  )}
                </div>

              {/* Conditional Email Input */}
                <div>
                  <label className="font-semibold text-sm text-gray-700 title-animate">
                    {formContent.fields.email}
                  </label>
                  <input
                    type="email"
                    {...register("email", {
                      required: "This field is required",
                      pattern: {
                        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                        message: "Please enter a valid email address",
                      },
                    })}
                    className="focus:border-blue-500 border text-start text-sm text-gray-600 rounded-lg px-7 py-3 fade-in"
                    placeholder={formContent.placeholders.email}
                  />
                  {errors.email && (
                    <p className="text-red-500 text-sm desc-animate">
                      {errors.email.message}
                    </p>
                  )}
                </div>
            </div>

            {/* Submit Button */}
          <button
  type="submit"
  className="relative w-full text-white py-3 px-4 flex items-center justify-between text-lg font-semibold rounded-lg mt-4 overflow-hidden group"
>
  {/* Default Background */}
  <span className="absolute inset-0 bg-gray-900 transition-all duration-500 ease-in-out group-hover:opacity-0"></span>

  {/* Hover Background */}
  <span className="absolute inset-0 bg-green-500 opacity-0 transition-all duration-500 ease-in-out group-hover:opacity-100"></span>

  {/* Button Content */}
  <span className="relative z-10 flex items- justify-right w-full">
    <span className="slide-up">{formContent.buttons.submit}</span> 
    {formContent.buttons.submit}
    <span className="w-8 h-8 flex items-center justify-center bg-white rounded-full ml-2">
      <ArrowRight className="w-4 h-4 text-gray-900" />
    </span>
  </span>
</button>
          </form>
        </div>
      </div>
      <ToastContainer />
    </div>
  );
};

export default CbgQuoteForm;
